import React, { useRef, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  IconButton,
  styled,
} from "@mui/material";
import {
  Close as CloseIcon,
  AccountBalanceWallet as WalletIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";

// Types
import {
  CostDetails,
  RegenerateItem,
} from "./types/MeGenTypes";

// ─── Styled Components ───────────────────────────────────────────────────────

const CostModalOverlay = styled(Box)({
  position: "fixed",
  inset: 0,
  zIndex: 1300,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(15, 23, 42, 0.5)",
  backdropFilter: "blur(4px)",
});

const CostModalCard = styled(Box)({
  backgroundColor: "white",
  borderRadius: "1rem",
  boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
  width: "100%",
  maxWidth: "600px",
  maxHeight: "85vh",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
});

// ─── Component ───────────────────────────────────────────────────────────────

const CardMeGenCostModal: React.FC<{
  open: boolean;
  costDetails: CostDetails | null;
  regenerateItems: RegenerateItem[];
  sceneIndexMap?: Record<string, number>;
  onClose: () => void;
  onConfirm: () => void;
}> = (props) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (props.open && scrollRef.current) {
      // Use requestAnimationFrame to ensure DOM is fully rendered before scrolling
      requestAnimationFrame(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
      });
    }
  }, [props.open]);

  if (!props.open || !props.costDetails) return null;

  const {
    can_use,
    total_scene_credit,
    previous_wallet_balance,
    total_default_scene_credit,
    total_video_scene_credit,
    message,
  } = props.costDetails;

  const totalCost = parseFloat(total_scene_credit || "0");
  const prevBalance = parseFloat(previous_wallet_balance || "0");
  const remaining = prevBalance - totalCost;
  const hasChanges = props.regenerateItems.length > 0;

  return (
    <CostModalOverlay id="megen-cost-modal-root" onClick={props.onClose}>
      <CostModalCard onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <Box sx={{
          px: 3, py: 2.5,
          borderBottom: "1px solid #f1f5f9",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{
              width: 40, height: 40, borderRadius: "50%",
              display: "flex", alignItems: "center", justifyContent: "center",
              backgroundColor: can_use ? "#fdf2f8" : "#fef2f2",
              color: can_use ? "#db2777" : "#ef4444",
            }}>
              <WalletIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#1e293b" }}>
                ตรวจสอบ Credit
              </Typography>
              <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8" }}>
                {hasChanges ? `${props.regenerateItems.length} scene จะถูก regenerate` : "ไม่มีการเปลี่ยนแปลง"}
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={props.onClose} sx={{ color: "#94a3b8" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box ref={scrollRef} sx={{ px: 3, py: 2.5, overflowY: "auto", flex: 1 }}>
          {/* Insufficient balance warning */}
          {!can_use && (
            <Box sx={{
              mb: 2, p: 1.5,
              backgroundColor: "#fef2f2", border: "1px solid #fecaca",
              borderRadius: "0.5rem",
              display: "flex", alignItems: "flex-start", gap: 1,
            }}>
              <WarningIcon sx={{ fontSize: 16, color: "#ef4444", mt: 0.25, flexShrink: 0 }} />
              <Box>
                <Typography sx={{ fontSize: "0.8125rem", fontWeight: 700, color: "#dc2626" }}>
                  Credit ไม่เพียงพอ
                </Typography>
                <Typography sx={{ fontSize: "0.75rem", color: "#ef4444" }}>
                  {message || "กรุณาเติม Credit ก่อนดำเนินการ"}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Regenerate items list */}
          {hasChanges && (
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#64748b", mb: 1, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                รายการที่จะ Regenerate
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                {props.regenerateItems.map((ri) => {
                  const parts = [
                    ri.audio && "Audio",
                    ri.image && "Image",
                    ri.video && "Video",
                  ].filter(Boolean);

                  const sceneNum = props.sceneIndexMap?.[ri.id];
                  const sceneLabel = sceneNum != null ? `Scene ${sceneNum}` : `Scene (id: ${ri.id})`;

                  return (
                    <Box key={ri.id} sx={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      px: 1.5, py: 1,
                      backgroundColor: "#f8fafc", borderRadius: "0.375rem",
                      border: "1px solid #f1f5f9",
                    }}>
                      <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: "#1e293b" }}>
                        {sceneLabel}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        {parts.map((p: any) => (
                          <Box key={p} sx={{
                            fontSize: "0.65rem", fontWeight: 700,
                            px: 0.75, py: 0.25,
                            backgroundColor: p === "Video" ? "#f5f3ff" : p === "Audio" ? "#eff6ff" : "#fdf2f8",
                            color: p === "Video" ? "#7c3aed" : p === "Audio" ? "#3b82f6" : "#db2777",
                            borderRadius: "9999px",
                            border: `1px solid ${p === "Video" ? "#ddd6fe" : p === "Audio" ? "#bfdbfe" : "#f9a8d4"}`,
                          }}>
                            {p}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* Cost breakdown */}
          <Box sx={{
            backgroundColor: "#f8fafc", borderRadius: "0.5rem",
            border: "1px solid #f1f5f9", overflow: "hidden", mb: 2.5,
          }}>
            <Box sx={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              px: 2, py: 1.5, borderBottom: "1px solid #f1f5f9",
            }}>
              <Typography sx={{ fontSize: "0.875rem", color: "#64748b" }}>
                Credit ที่ต้องใช้
              </Typography>
              <Typography sx={{ fontSize: "1.25rem", fontWeight: 900, color: "#db2777" }}>
                {total_scene_credit}
              </Typography>
            </Box>

            {(total_default_scene_credit || total_video_scene_credit) && (
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1px", backgroundColor: "#f1f5f9" }}>
                <Box sx={{ px: 2, py: 1, backgroundColor: "#f8fafc" }}>
                  <Typography sx={{ fontSize: "0.65rem", color: "#94a3b8", fontWeight: 700, textTransform: "uppercase" }}>
                    Standard
                  </Typography>
                  <Typography sx={{ fontSize: "0.875rem", fontWeight: 700, color: "#475569" }}>
                    {total_default_scene_credit || "0.00"}
                  </Typography>
                </Box>
                <Box sx={{ px: 2, py: 1, backgroundColor: "#f8fafc" }}>
                  <Typography sx={{ fontSize: "0.65rem", color: "#94a3b8", fontWeight: 700, textTransform: "uppercase" }}>
                    Video
                  </Typography>
                  <Typography sx={{ fontSize: "0.875rem", fontWeight: 700, color: "#475569" }}>
                    {total_video_scene_credit || "0.00"}
                  </Typography>
                </Box>
              </Box>
            )}

            {previous_wallet_balance && (
              <Box sx={{ px: 2, py: 1.5, borderTop: "1px solid #f1f5f9" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                  <Typography sx={{ fontSize: "0.8125rem", color: "#64748b" }}>ยอด Wallet</Typography>
                  <Typography sx={{ fontSize: "0.8125rem", color: "#64748b" }}>{previous_wallet_balance}</Typography>
                </Box>
                <Box sx={{
                  display: "flex", justifyContent: "space-between",
                  px: 1.5, py: 1,
                  backgroundColor: remaining >= 0 ? "#f0fdf4" : "#fef2f2",
                  borderRadius: "0.375rem",
                  border: `1px solid ${remaining >= 0 ? "#bbf7d0" : "#fecaca"}`,
                }}>
                  <Typography sx={{ fontSize: "0.875rem", fontWeight: 700, color: remaining >= 0 ? "#16a34a" : "#dc2626" }}>
                    คงเหลือ
                  </Typography>
                  <Typography sx={{ fontSize: "0.875rem", fontWeight: 700, color: remaining >= 0 ? "#16a34a" : "#dc2626" }}>
                    {remaining.toFixed(2)}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          {/* No changes info */}
          {!hasChanges && can_use && (
            <Box sx={{
              mb: 2, p: 1.5,
              backgroundColor: "#f0fdf4", border: "1px solid #bbf7d0",
              borderRadius: "0.5rem",
            }}>
              <Typography sx={{ fontSize: "0.8125rem", color: "#16a34a", fontWeight: 500, textAlign: "center" }}>
                ✓ ไม่มีการเปลี่ยนแปลง — จะทำการ Generate ใหม่ทั้งหมด
              </Typography>
            </Box>
          )}

          {/* Actions */}
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button
              fullWidth
              onClick={props.onClose}
              sx={{
                textTransform: "none", fontWeight: 600,
                border: "1px solid #e2e8f0", color: "#64748b",
                borderRadius: "9999px", py: 1.25,
                "&:hover": { backgroundColor: "#f8fafc" },
              }}
            >
              ยกเลิก
            </Button>
            <Button
              fullWidth
              onClick={props.onConfirm}
              disabled={!can_use}
              sx={{
                textTransform: "none", fontWeight: 700,
                backgroundColor: can_use ? "#db2777" : "#94a3b8",
                color: "white",
                borderRadius: "9999px", py: 1.25,
                boxShadow: can_use ? "0 4px 14px rgba(219,39,119,0.3)" : "none",
                "&:hover": can_use ? { backgroundColor: "#be185d" } : {},
                "&:disabled": { backgroundColor: "#94a3b8", color: "white" },
              }}
            >
              ยืนยัน & สร้างวิดีโอ
            </Button>
          </Box>
        </Box>
      </CostModalCard>
    </CostModalOverlay>
  );
};

export default CardMeGenCostModal;
